import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  createGroup,
  deleteGroup,
  getGroupsHome,
  updateGroup,
} from "../../../../../services/groupServices";

import {
  buildGroupesStats,
  filterAndSortGroupes,
  isDemoUser,
  normalizeGroupPayload,
  safeJsonParse,
} from "../utils/groupesUtils";

const INITIAL_FILTERS = {
  query: "",
  status: "all",
  sortOrder: "ordre_asc",
};

export default function useGroupes() {
  const [groupes, setGroupes] = useState([]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [drawerMode, setDrawerMode] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);

  const [deleteCandidateId, setDeleteCandidateId] = useState(null);
  const [isDemoMode, setIsDemoMode] = useState(false);

  const setFilter = useCallback((name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const loadUserMode = useCallback(() => {
    const user =
      safeJsonParse(localStorage.getItem("user"), null) ||
      safeJsonParse(localStorage.getItem("userInfo"), null);

    setIsDemoMode(isDemoUser(user));
  }, []);

  const refreshGroupes = useCallback(async () => {
    setLoading(true);

    try {
      const data = await getGroupsHome();
      setGroupes(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur lors de la récupération des groupes :", error);
      toast.error("Erreur lors de la récupération des groupes.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUserMode();
    refreshGroupes();
  }, [loadUserMode, refreshGroupes]);

  const guardDemoMode = useCallback(() => {
    if (!isDemoMode) return false;

    toast.info("Fonction désactivée en mode démonstration.");
    return true;
  }, [isDemoMode]);

  const filteredGroupes = useMemo(
    () => filterAndSortGroupes(groupes, filters),
    [groupes, filters]
  );

  const stats = useMemo(
    () => buildGroupesStats(groupes, filteredGroupes),
    [groupes, filteredGroupes]
  );

  const openCreateDrawer = useCallback(() => {
    if (guardDemoMode()) return;

    setSelectedGroup(null);
    setDrawerMode("create");
  }, [guardDemoMode]);

  const openEditDrawer = useCallback(
    (group) => {
      if (guardDemoMode()) return;

      setSelectedGroup(group);
      setDrawerMode("edit");
    },
    [guardDemoMode]
  );

  const openDetailsDrawer = useCallback((group) => {
    setSelectedGroup(group);
    setDrawerMode("details");
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerMode(null);
    setSelectedGroup(null);
  }, []);

  const createNewGroup = useCallback(
    async (formData) => {
      if (guardDemoMode()) return;

      setSaving(true);

      try {
        const payload = normalizeGroupPayload(formData);

        await createGroup(payload);

        toast.success("Groupe créé avec succès.");
        closeDrawer();
        await refreshGroupes();
      } catch (error) {
        console.error("Erreur lors de la création du groupe :", error);
        toast.error("Erreur lors de la création du groupe.");
      } finally {
        setSaving(false);
      }
    },
    [closeDrawer, guardDemoMode, refreshGroupes]
  );

  const saveGroup = useCallback(
    async (formData) => {
      if (guardDemoMode()) return;

      setSaving(true);

      try {
        const payload = normalizeGroupPayload(formData);

        await updateGroup(payload);

        toast.success("Groupe modifié avec succès.");
        closeDrawer();
        await refreshGroupes();
      } catch (error) {
        console.error("Erreur lors de la modification du groupe :", error);
        toast.error("Erreur lors de la modification du groupe.");
      } finally {
        setSaving(false);
      }
    },
    [closeDrawer, guardDemoMode, refreshGroupes]
  );

  const askDelete = useCallback(
    (groupId) => {
      if (guardDemoMode()) return;
      setDeleteCandidateId(groupId);
    },
    [guardDemoMode]
  );

  const cancelDelete = useCallback(() => {
    setDeleteCandidateId(null);
  }, []);

  const confirmDelete = useCallback(
    async (groupId) => {
      if (guardDemoMode()) return;

      try {
        await deleteGroup(groupId);

        toast.success("Groupe supprimé avec succès.");
        setDeleteCandidateId(null);
        await refreshGroupes();
      } catch (error) {
        console.error("Erreur lors de la suppression du groupe :", error);
        toast.error("Erreur lors de la suppression du groupe.");
      }
    },
    [guardDemoMode, refreshGroupes]
  );

  return {
    loading,
    saving,
    filters,
    setFilter,
    groupes,
    filteredGroupes,
    selectedGroup,
    drawerMode,
    deleteCandidateId,
    stats,
    actions: {
      refreshGroupes,
      openCreateDrawer,
      openEditDrawer,
      openDetailsDrawer,
      closeDrawer,
      createGroup: createNewGroup,
      updateGroup: saveGroup,
      askDelete,
      cancelDelete,
      confirmDelete,
    },
  };
}